import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { login } from '../services/authService';

/* ─── Global CSS ─────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html, body, #root {
    height: 100%;
    overflow: hidden;
  }

  /* Keyframes */
  @keyframes rotateOrbit {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes pulseGlow {
    0%, 100% { opacity: 0.4; }
    50%       { opacity: 0.75; }
  }

  /* ── Page ── */
  .gg-page {
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #051021;
    font-family: 'Plus Jakarta Sans', sans-serif;
    position: relative;
  }

  /* ── Glow overlay ── */
  .gg-glow {
    position: fixed;
    inset: 0;
    background: radial-gradient(ellipse 55% 50% at 50% 50%, rgba(102,178,255,0.05) 0%, transparent 70%);
    animation: pulseGlow 10s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }

  /* ── Orbit ── */
  .gg-orbit-wrap {
    position: absolute;
    top: 50%; left: 50%;
    width: min(820px, 90vmin);
    height: min(820px, 90vmin);
    margin-top: calc(min(820px, 90vmin) / -2);
    margin-left: calc(min(820px, 90vmin) / -2);
    pointer-events: none;
    z-index: 0;
  }
  .gg-orbit-ring {
    position: absolute;
    top: 50%; left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    border: 1px solid rgba(102,178,255,0.06);
  }
  .gg-orbit-animator {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    animation: rotateOrbit 42s linear infinite;
  }
  .gg-orbit-plane {
    position: absolute;
    top: 0%; left: 50%;
    transform: translate(-50%, -50%) rotate(90deg);
    opacity: 0.5;
    filter: drop-shadow(0 0 6px rgba(102,178,255,0.3));
  }

  /* ── Scroll container (centers card, no overflow) ── */
  .gg-scroll {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 440px;
    max-height: 100vh;
    overflow: hidden;
    animation: fadeSlideUp 0.5s ease both;
    padding: 0 16px;
  }

  /* ── Logo ── */
  .gg-logo {
    text-align: center;
    margin-bottom: 20px;
  }
  .gg-pin-badge {
    width: 56px; height: 56px;
    border-radius: 14px;
    background: rgba(102,178,255,0.1);
    border: 1px solid rgba(102,178,255,0.2);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 12px;
    box-shadow: 0 0 20px rgba(102,178,255,0.1);
  }
  .gg-wordmark {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(22px, 4vw, 30px);
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.3px;
    line-height: 1;
  }
  .gg-wordmark-gold { color: #FFCB2B; }
  .gg-admin-label {
    font-size: 10px;
    font-weight: 700;
    color: rgba(255,255,255,0.32);
    letter-spacing: 2.8px;
    text-transform: uppercase;
    margin-top: 6px;
  }

  /* ── Glass Card ── */
  .gg-card {
    background: rgba(8,22,55,0.82);
    border: 1px solid rgba(102,178,255,0.18);
    border-radius: 16px;
    padding: clamp(18px, 3.5vh, 30px) clamp(16px, 4vw, 28px) clamp(14px, 2.5vh, 24px);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    box-shadow: 0 0 0 1px rgba(102,178,255,0.05), 0 20px 60px rgba(0,0,0,0.5);
  }

  /* ── Form labels ── */
  .gg-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: rgba(255,255,255,0.42);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
  }

  /* ── Inputs ── */
  .gg-input {
    width: 100%;
    padding: clamp(9px, 1.3vh, 12px) 14px clamp(9px, 1.3vh, 12px) 40px;
    background: rgba(5,16,40,0.7);
    border: 1.5px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    font-size: clamp(13px, 1.8vw, 14px);
    color: #E8EEFF;
    font-family: 'Plus Jakarta Sans', sans-serif;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .gg-input::placeholder { color: rgba(255,255,255,0.22); }
  .gg-input:focus {
    border-color: #66B2FF;
    box-shadow: 0 0 0 3px rgba(102,178,255,0.15);
    outline: none;
  }

  /* ── Submit button ── */
  #gg-signin-btn {
    width: 100%;
    padding: clamp(10px, 1.4vh, 13px);
    background: #66B2FF;
    border: none;
    border-radius: 8px;
    font-size: clamp(13px, 1.8vw, 14px);
    font-weight: 700;
    color: #001D35;
    cursor: pointer;
    font-family: 'Plus Jakarta Sans', sans-serif;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 18px rgba(102,178,255,0.3);
    letter-spacing: 0.1px;
  }
  #gg-signin-btn:not(:disabled):hover {
    background: #7ec4ff;
    box-shadow: 0 6px 28px rgba(102,178,255,0.45);
    transform: translateY(-1px);
  }
  #gg-signin-btn:disabled {
    background: rgba(102,178,255,0.35);
    cursor: not-allowed;
  }

  /* ── Status bar ── */
  .gg-status-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: clamp(12px, 2vh, 18px);
    padding-top: clamp(10px, 1.5vh, 14px);
    border-top: 1px solid rgba(255,255,255,0.06);
  }

  /* ── Responsive tweaks ── */
  @media (max-height: 700px) {
    .gg-logo { margin-bottom: 10px; }
    .gg-pin-badge { width: 44px; height: 44px; margin-bottom: 8px; }
    .gg-card { gap: 0; }
  }
  @media (max-height: 600px) {
    .gg-pin-badge { display: none; }
    .gg-logo { margin-bottom: 8px; }
  }
  @media (max-width: 400px) {
    .gg-scroll { padding: 0 10px; }
  }
`;

/* ─── Icons ──────────────────────────────────────────────────── */
const PinIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="#66B2FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PlaneSVG = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="#66B2FF" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
  </svg>
);

/* ═══════════════════════════════════════════
   Login Component
═══════════════════════════════════════════ */
const Login = ({ onLogin }) => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  /* Inject CSS */
  useEffect(() => {
    const id = 'gg-login-css';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && (res.status === 200 || res.status === 201)) { onLogin(); }
      else { setError(res?.data?.message || 'Login failed. Please try again.'); }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Connection failed. Try again later.');
    } finally { setLoading(false); }
  };

  return (
    <div className="gg-page">
      <div className="gg-glow" />

      {/* Orbit */}
      <div className="gg-orbit-wrap">
        <div className="gg-orbit-ring" style={{ width: '100%', height: '100%' }} />
        <div className="gg-orbit-ring" style={{ width: '68%', height: '68%' }} />
        <div className="gg-orbit-animator">
          <div className="gg-orbit-plane"><PlaneSVG /></div>
        </div>
      </div>

      {/* Content */}
      <div className="gg-scroll">

        {/* Logo */}
        <div className="gg-logo">
          <div className="gg-pin-badge"><PinIcon /></div>
          <div className="gg-wordmark">
            Gate<span className="gg-wordmark-gold">Guide</span>
          </div>
          <div className="gg-admin-label">Admin Panel</div>
        </div>

        {/* Card */}
        <div className="gg-card">

          {/* Heading */}
          <div style={{ marginBottom: 'clamp(14px,2.2vh,22px)' }}>
            <h1 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px,3vw,22px)',
              fontWeight: 700, color: '#F0F4FF',
              margin: 0, marginBottom: 4,
            }}>
              Welcome back
            </h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', margin: 0 }}>
              Sign in to manage GateGuide
            </p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="on" noValidate>

            {/* Email */}
            <div style={{ marginBottom: 'clamp(10px,1.6vh,14px)' }}>
              <label htmlFor="login-email" className="gg-label">Email Address</label>
              <div style={{ position: 'relative' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(102,178,255,0.5)" strokeWidth="1.8" strokeLinecap="round"
                  style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <input
                  id="login-email" className="gg-input"
                  type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@gateguide.io"
                  autoComplete="email"
                  style={{ borderColor: email ? 'rgba(102,178,255,0.38)' : undefined }}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 'clamp(14px,2.2vh,20px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label htmlFor="login-password" className="gg-label" style={{ marginBottom: 0 }}>Password</label>
                <Link to="/auth/forgot-password" style={{ fontSize: 12, color: '#66B2FF', fontWeight: 500, textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </div>
              <div style={{ position: 'relative' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(102,178,255,0.5)" strokeWidth="1.8" strokeLinecap="round"
                  style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  id="login-password" className="gg-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  style={{ padding: 'clamp(9px,1.3vh,12px) 42px clamp(9px,1.3vh,12px) 40px', borderColor: password ? 'rgba(102,178,255,0.38)' : undefined }}
                />
                <button type="button" id="toggle-password"
                  onClick={() => setShowPass(v => !v)}
                  style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.28)', padding: 4, lineHeight: 0 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    {showPass
                      ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>
                      : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                    }
                  </svg>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ background: 'rgba(255,75,75,0.1)', border: '1px solid rgba(255,75,75,0.28)', borderRadius: 8, padding: '9px 13px', marginBottom: 12, fontSize: 13, color: '#FF7B7B', display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            {/* Submit */}
            <button id="gg-signin-btn" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Signing in…
                </>
              ) : (
                <>
                  Sign In to Admin Panel
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 'clamp(12px,1.8vh,20px) 0 clamp(10px,1.5vh,16px)' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.2)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>SECURE LOGIN</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
          </div>

          {/* Security note */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(102,178,255,0.05)', border: '1px solid rgba(102,178,255,0.1)', borderRadius: 8, padding: 'clamp(8px,1.2vh,11px) 13px' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#66B2FF" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 2 }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.32)', lineHeight: 1.6 }}>
              Authorized GateGuide administrators only. All access is monitored and logged.
            </p>
          </div>

          {/* Status bar */}
          <div className="gg-status-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#FFCB2B', boxShadow: '0 0 6px rgba(255,203,43,0.7)' }} />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>System Online</span>
            </div>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontFamily: "'Space Grotesk', monospace", letterSpacing: '0.5px' }}>v4.2.0-secure</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
